import {
  UploadToMediaHouseDocument,
  type UploadToMediaHouseMutation,
  type UploadToMediaHouseMutationVariables,
} from "@/generated/graphql";
import { Button, IconButton } from "@ai-teach/ui-web";
import { useMutation } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { FiX } from "react-icons/fi";
import { UploadPreviewPanel } from "./UploadPreviewPanel";

interface MediaUploadModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Media type to upload */
  mediaType: "image" | "video" | "audio";
  /** Current folder ID (null for root) */
  folderId: string | null;
  /** Refetch query name */
  refetchQuery: string;
}

/**
 * File with metadata for upload
 */
export interface FileWithMetadata {
  file: File;
  audioText: string;
  preview: string; // Object URL for preview
  tags: string[]; // Per-file tags
}

/**
 * MediaUploadModal - Generic upload modal for all media types
 *
 * Handles file upload, metadata, and tags.
 */
export function MediaUploadModal({
  isOpen,
  onClose,
  mediaType,
  folderId,
  refetchQuery,
}: MediaUploadModalProps) {
  // State
  const [files, setFiles] = useState<FileWithMetadata[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Mutations
  const [uploadMedia, { loading }] = useMutation<
    UploadToMediaHouseMutation,
    UploadToMediaHouseMutationVariables
  >(UploadToMediaHouseDocument, {
    refetchQueries: [refetchQuery],
  });

  // Reset state when modal closes
  // biome-ignore lint/correctness/useExhaustiveDependencies: Only run cleanup when modal closes, not when files change
  useEffect(() => {
    if (!isOpen) {
      // Revoke object URLs to prevent memory leaks
      for (const f of files) {
        URL.revokeObjectURL(f.preview);
      }
      setFiles([]);
      setError(null);
    }
  }, [isOpen]);

  // Handle file selection
  const handleFilesChange = (newFiles: File[]) => {
    const validFiles: FileWithMetadata[] = [];

    for (const file of newFiles) {
      validFiles.push({
        file,
        audioText: "",
        preview: URL.createObjectURL(file),
        tags: [],
      });
    }

    setFiles((prev) => [...prev, ...validFiles]);
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) {
      handleFilesChange(dropped);
    }
  };

  // Handle file removal
  const handleRemoveFile = (index: number) => {
    setFiles((prev) => {
      const newFiles = [...prev];
      const removed = newFiles.splice(index, 1)[0];
      if (removed) {
        URL.revokeObjectURL(removed.preview);
      }
      return newFiles;
    });
  };

  // Handle metadata update for a specific file
  const handleUpdateMetadata = (index: number, updates: Partial<FileWithMetadata>) => {
    setFiles((prev) => {
      const newFiles = [...prev];
      const file = newFiles[index];
      if (file) {
        newFiles[index] = { ...file, ...updates };
      }
      return newFiles;
    });
  };

  // Handle save
  const handleSave = async () => {
    if (files.length === 0) {
      setError("Please select at least one file");
      return;
    }

    // Check that each file has at least one tag
    const filesWithoutTags = files.filter((f) => f.tags.length === 0);
    if (filesWithoutTags.length > 0) {
      setError(
        `Please add at least one tag to each file (${filesWithoutTags.length} file${filesWithoutTags.length > 1 ? "s" : ""} missing tags)`
      );
      return;
    }

    setError(null);

    try {
      for (const fileData of files) {
        // Upload file with per-file tags
        await uploadMedia({
          variables: {
            file: fileData.file,
            input: {
              folderId,
              type: mediaType,
              tags: fileData.tags,
              ...(mediaType === "audio" || mediaType === "video"
                ? { audioText: fileData.audioText }
                : {}),
            },
          },
        });
      }

      toast.success(`${files.length} ${files.length === 1 ? mediaType : `${mediaType}s`} uploaded`);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed";
      setError(message);
      toast.error(message);
    }
  };

  const getAcceptedTypes = () => {
    switch (mediaType) {
      case "image":
        return "image/*";
      case "video":
        return "video/*";
      case "audio":
        return "audio/*";
      default:
        return "*/*";
    }
  };

  const getTitle = () => {
    switch (mediaType) {
      case "image":
        return "Upload Images";
      case "video":
        return "Upload Videos";
      case "audio":
        return "Upload Audio";
      default:
        return "Upload Media";
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="!mt-0 fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={loading ? undefined : onClose}
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="h6">{getTitle()}</h2>
          <IconButton onClick={onClose} disabled={loading} variant="ghost" aria-label="Close modal">
            <FiX className="h-5 w-5" />
          </IconButton>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* Upload area */}
          {files.length === 0 ? (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-12 transition-colors ${
                isDragging ? "border-primary-400 bg-primary-50" : "border-gray-300 bg-gray-50"
              }`}
            >
              <p className="mb-4 text-sm text-gray-600">
                {isDragging
                  ? `Drop ${mediaType} files here`
                  : `Drag and drop ${mediaType} files here, or click to select`}
              </p>
              <label className="cursor-pointer rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700">
                Select Files
                <input
                  type="file"
                  multiple
                  accept={getAcceptedTypes()}
                  onChange={(e) => {
                    if (e.target.files) {
                      handleFilesChange(Array.from(e.target.files));
                    }
                    e.target.value = "";
                  }}
                  className="sr-only"
                />
              </label>
            </div>
          ) : (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`flex items-center justify-between rounded-lg border-2 border-dashed px-4 py-3 transition-colors ${
                isDragging ? "border-primary-400 bg-primary-50" : "border-gray-300 bg-gray-50"
              }`}
            >
              <span className="text-sm text-gray-600">
                {isDragging
                  ? "Drop to add files"
                  : `${files.length} ${files.length === 1 ? "file" : "files"} selected`}
              </span>
              <label className="cursor-pointer rounded-md bg-white px-3 py-1.5 label shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                Add more
                <input
                  type="file"
                  multiple
                  accept={getAcceptedTypes()}
                  onChange={(e) => {
                    if (e.target.files) {
                      handleFilesChange(Array.from(e.target.files));
                    }
                    e.target.value = "";
                  }}
                  className="sr-only"
                />
              </label>
            </div>
          )}

          {/* File previews */}
          {files.length > 0 && (
            <UploadPreviewPanel
              files={files}
              mediaType={mediaType}
              onRemoveFile={handleRemoveFile}
              onUpdateMetadata={handleUpdateMetadata}
            />
          )}

          {/* Error message */}
          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
          <Button onClick={onClose} disabled={loading} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading || files.length === 0} variant="primary">
            {loading ? "Uploading..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
